import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useEstimate } from "../store/EstimateContext";
import { CURRENCIES, DEFAULT_ROLES, findCurrency } from "../lib/s1000dData";
import { fmtCurrency, fmtPercent } from "../lib/calculations";
import { Plus, Trash2, Save, X, CheckSquare, Pencil } from "lucide-react";
import { toast } from "sonner";
import HelpIconButton from "./HelpIconButton";

function emptyCard() {
  return {
    id: null,
    name: "",
    description: "",
    currency: { ...CURRENCIES[0] },
    roles: DEFAULT_ROLES.map((r) => ({ ...r })),
    dmDefaultHours: { simple: 6, intermediate: 8, complex: 12 },
    illustrationDefaultHours: { simple: 4, intermediate: 8, complex: 12 },
    overheadPercent: 0,
    contingencyPercent: 0,
  };
}

function CardEditor({ card, onSave, onCancel }) {
  const [c, setC] = useState(card);

  const setField = (path, value) => {
    setC((cur) => {
      const next = { ...cur };
      const keys = path.split(".");
      let ref = next;
      for (let i = 0; i < keys.length - 1; i++) {
        ref[keys[i]] = { ...ref[keys[i]] };
        ref = ref[keys[i]];
      }
      ref[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const updateRole = (idx, field, value) =>
    setC((cur) => ({
      ...cur,
      roles: cur.roles.map((r, i) =>
        i === idx ? { ...r, [field]: field === "name" ? value : Number(value) || 0 } : r
      ),
    }));
  const addRole = () =>
    setC((cur) => ({ ...cur, roles: [...cur.roles, { name: "New Role", hourlyRate: 100, effortPercent: 0 }] }));
  const removeRole = (idx) => setC((cur) => ({ ...cur, roles: cur.roles.filter((_, i) => i !== idx) }));

  const effortSum = c.roles.reduce((s, r) => s + Number(r.effortPercent || 0), 0);
  const effortValid = Math.abs(effortSum - 100) < 0.01;

  const handleSave = () => {
    if (!c.name.trim()) {
      toast.error("Please enter a rate card name");
      return;
    }
    if (!effortValid) {
      if (!window.confirm(`Effort total is ${effortSum.toFixed(1)}% — should be 100%. Save anyway?`)) return;
    }
    onSave(c);
  };

  return (
    <div className="bg-white border border-[#002FA7] p-5 space-y-4" data-testid="ratecard-editor">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#002FA7]">
            {card.id ? "Edit Rate Card" : "New Rate Card"}
          </p>
          <h3 className="font-heading text-xl tracking-tight font-bold text-black">
            {card.id ? c.name || "—" : "Create a rate card"}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onCancel} data-testid="ratecard-cancel-btn"
            className="bg-white border border-gray-300 text-black hover:bg-gray-100 rounded-none px-3 py-2 font-bold text-sm inline-flex items-center gap-2">
            <X size={14} /> Cancel
          </button>
          <button onClick={handleSave} data-testid="ratecard-save-btn"
            className="bg-black text-white hover:bg-[#002FA7] rounded-none px-3 py-2 font-bold text-sm inline-flex items-center gap-2">
            <Save size={14} /> Save
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500 block">Name</label>
          <input data-testid="ratecard-name-input" value={c.name}
            onChange={(e) => setField("name", e.target.value)}
            placeholder="e.g. Defense Tier 1 · 2026"
            className="border border-gray-300 rounded-none px-3 py-1.5 font-mono text-sm w-full mt-1" />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500 block">Description</label>
          <input data-testid="ratecard-description-input" value={c.description}
            onChange={(e) => setField("description", e.target.value)}
            placeholder="Optional notes (audience, validity…)"
            className="border border-gray-300 rounded-none px-3 py-1.5 font-mono text-sm w-full mt-1" />
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500 block">Currency</label>
          <select data-testid="ratecard-currency-select" value={c.currency?.code || "USD"}
            onChange={(e) => setField("currency", { ...findCurrency(e.target.value) })}
            className="border border-gray-300 rounded-none px-2 py-1.5 font-mono text-sm bg-white w-full mt-1">
            <optgroup label="FX-supported">
              {CURRENCIES.filter((cur) => cur.fxSupported).map((cur) => (
                <option key={cur.code} value={cur.code}>{`${cur.symbol} ${cur.code}`}</option>
              ))}
            </optgroup>
            <optgroup label="All other currencies">
              {CURRENCIES.filter((cur) => !cur.fxSupported).map((cur) => (
                <option key={cur.code} value={cur.code}>{`${cur.symbol} ${cur.code}`}</option>
              ))}
            </optgroup>
          </select>
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500 block">Overhead %</label>
          <input type="number" step="0.5" min="0" value={c.overheadPercent}
            onChange={(e) => setField("overheadPercent", Number(e.target.value) || 0)}
            data-testid="ratecard-overhead-input"
            className="border border-gray-300 rounded-none px-2 py-1.5 font-mono text-sm w-full mt-1 text-right" />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500 block">Contingency %</label>
          <input type="number" step="0.5" min="0" value={c.contingencyPercent}
            onChange={(e) => setField("contingencyPercent", Number(e.target.value) || 0)}
            data-testid="ratecard-contingency-input"
            className="border border-gray-300 rounded-none px-2 py-1.5 font-mono text-sm w-full mt-1 text-right" />
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">Effort Sum</p>
          <p className={"font-mono text-lg font-bold mt-1 " + (effortValid ? "text-black" : "text-[#FF2400]")}>
            {fmtPercent(effortSum)}
          </p>
        </div>
      </div>

      {/* Default Hours */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-gray-300 p-3">
          <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">DM Default Hours</p>
          <div className="grid grid-cols-3 gap-2 mt-2">
            {["simple", "intermediate", "complex"].map((lvl) => (
              <div key={lvl}>
                <label className="text-[10px] uppercase tracking-wider text-gray-500 block">{lvl}</label>
                <input type="number" min="0" step="0.5" value={c.dmDefaultHours[lvl]}
                  onChange={(e) => setField(`dmDefaultHours.${lvl}`, Number(e.target.value) || 0)}
                  data-testid={`ratecard-dm-${lvl}`}
                  className="border border-gray-300 rounded-none px-2 py-1 font-mono text-sm w-full text-right" />
              </div>
            ))}
          </div>
        </div>
        <div className="border border-gray-300 p-3">
          <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">Illustration Default Hours</p>
          <div className="grid grid-cols-3 gap-2 mt-2">
            {["simple", "intermediate", "complex"].map((lvl) => (
              <div key={lvl}>
                <label className="text-[10px] uppercase tracking-wider text-gray-500 block">{lvl}</label>
                <input type="number" min="0" step="0.5" value={c.illustrationDefaultHours[lvl]}
                  onChange={(e) => setField(`illustrationDefaultHours.${lvl}`, Number(e.target.value) || 0)}
                  data-testid={`ratecard-illus-${lvl}`}
                  className="border border-gray-300 rounded-none px-2 py-1 font-mono text-sm w-full text-right" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Roles */}
      <div className="border border-gray-300">
        <div className="flex items-center justify-between p-3 border-b border-gray-300">
          <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">Role Rates</p>
          <button onClick={addRole} data-testid="ratecard-add-role-btn"
            className="bg-black text-white hover:bg-[#002FA7] rounded-none px-2 py-1 font-bold text-xs inline-flex items-center gap-1">
            <Plus size={12} /> Add
          </button>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-100 text-black font-bold uppercase tracking-wider text-[10px]">
              <th className="py-2 px-3 text-left">Role</th>
              <th className="py-2 px-3 text-right">Hourly Rate</th>
              <th className="py-2 px-3 text-right">Effort %</th>
              <th className="py-2 px-3"></th>
            </tr>
          </thead>
          <tbody>
            {c.roles.map((r, idx) => (
              <tr key={idx} className="border-b border-gray-200">
                <td className="py-1.5 px-3">
                  <input value={r.name} onChange={(e) => updateRole(idx, "name", e.target.value)}
                    data-testid={`ratecard-role-${idx}-name`}
                    className="border border-gray-300 rounded-none px-2 py-1 text-sm w-full font-semibold" />
                </td>
                <td className="py-1.5 px-3 text-right">
                  <div className="inline-flex items-center gap-1">
                    <span className="font-mono text-xs text-gray-500">{c.currency?.symbol}</span>
                    <input type="number" min="0" step="0.5" value={r.hourlyRate}
                      onChange={(e) => updateRole(idx, "hourlyRate", e.target.value)}
                      data-testid={`ratecard-role-${idx}-rate`}
                      className="border border-gray-300 rounded-none px-2 py-1 font-mono text-sm w-24 text-right" />
                  </div>
                </td>
                <td className="py-1.5 px-3 text-right">
                  <input type="number" min="0" step="0.5" value={r.effortPercent}
                    onChange={(e) => updateRole(idx, "effortPercent", e.target.value)}
                    data-testid={`ratecard-role-${idx}-effort`}
                    className="border border-gray-300 rounded-none px-2 py-1 font-mono text-sm w-20 text-right" />
                </td>
                <td className="py-1.5 px-3 text-right">
                  <button onClick={() => removeRole(idx)} data-testid={`ratecard-role-${idx}-remove`}
                    className="text-gray-500 hover:text-[#FF2400] p-1">
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function RateCardsTab() {
  const { t } = useTranslation();
  const { rateCards, fetchRateCards, createRateCard, updateRateCard, deleteRateCard, applyRateCard, submitRateCard, approveRateCard, rejectRateCard } = useEstimate();
  const [editing, setEditing] = useState(null);

  const startNew = () => setEditing(emptyCard());
  const startEdit = (c) => setEditing({ ...c });

  const onSave = async (c) => {
    try {
      const payload = {
        name: c.name,
        description: c.description,
        currency: c.currency,
        roles: c.roles,
        dmDefaultHours: c.dmDefaultHours,
        illustrationDefaultHours: c.illustrationDefaultHours,
        overheadPercent: Number(c.overheadPercent) || 0,
        contingencyPercent: Number(c.contingencyPercent) || 0,
      };
      if (c.id) await updateRateCard(c.id, payload);
      else await createRateCard(payload);
      toast.success("Rate card saved");
      setEditing(null);
    } catch {
      toast.error("Failed to save rate card");
    }
  };

  const onDelete = async (c) => {
    if (!window.confirm(`Delete rate card "${c.name}"? This cannot be undone.`)) return;
    try {
      await deleteRateCard(c.id);
      toast.success("Rate card deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const onApply = (c) => {
    applyRateCard(c);
    toast.success("Rate card applied", {
      description: `${c.name} — rates, currency, overhead, contingency and default hours applied to current estimate.`,
    });
  };

  const onSubmitForApproval = async (c) => {
    const who = window.prompt("Submit for approval — your name / email:", "");
    if (who === null) return;
    try {
      await submitRateCard(c.id, who.trim() || "anonymous");
      toast.success(`Rate card "${c.name}" submitted for approval`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Submit failed";
      toast.error(msg);
    }
  };

  const onApprove = async (c) => {
    const who = window.prompt("Approve — your name / email:", "");
    if (who === null) return;
    try {
      await approveRateCard(c.id, who.trim() || "anonymous");
      toast.success(`Rate card "${c.name}" approved`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Approve failed";
      toast.error(msg);
    }
  };

  const onReject = async (c) => {
    const reason = window.prompt("Reject — reason:", "");
    if (reason === null) return;
    try {
      await rejectRateCard(c.id, reason);
      toast.success(`Rate card "${c.name}" rejected`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Reject failed";
      toast.error(msg);
    }
  };

  return (
    <section className="space-y-4" data-testid="ratecards-tab">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
            {t("ratecards.section")}
          </p>
          <h2 className="font-heading text-2xl tracking-tight font-bold text-black flex items-center gap-2">
            {t("ratecards.title")}
            <HelpIconButton anchor="ch3" testId="help-icon-ratecards" />
          </h2>
          <p className="text-sm text-gray-600 mt-1 max-w-2xl">
            {t("ratecards.description")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchRateCards} data-testid="ratecards-refresh-btn"
            className="bg-white border border-gray-300 text-black hover:bg-gray-100 rounded-none px-3 py-2 font-bold text-sm">
            Refresh
          </button>
          <button onClick={startNew} data-testid="ratecards-new-btn"
            className="bg-black text-white hover:bg-[#002FA7] rounded-none px-3 py-2 font-bold text-sm inline-flex items-center gap-2">
            <Plus size={14} /> New Rate Card
          </button>
        </div>
      </div>

      {editing && (
        <CardEditor card={editing} onSave={onSave} onCancel={() => setEditing(null)} />
      )}

      <div className="bg-white border border-gray-300 overflow-x-auto">
        <table className="w-full text-sm text-left border-collapse" data-testid="ratecards-table">
          <thead>
            <tr className="bg-gray-100 text-black font-bold uppercase tracking-wider text-[10px] border-y border-gray-300">
              <th className="py-3 px-3">Name</th>
              <th className="py-3 px-3">Status</th>
              <th className="py-3 px-3">Description</th>
              <th className="py-3 px-3">Currency</th>
              <th className="py-3 px-3 text-right">Overhead</th>
              <th className="py-3 px-3 text-right">Contingency</th>
              <th className="py-3 px-3 text-right">Roles</th>
              <th className="py-3 px-3 text-right">Avg Rate</th>
              <th className="py-3 px-3 w-80"></th>
            </tr>
          </thead>
          <tbody>
            {rateCards.length === 0 ? (
              <tr>
                <td colSpan={9} className="py-12 text-center text-gray-400 text-sm">
                  No rate cards yet. Click <span className="font-bold text-black">New Rate Card</span> to create your first preset.
                </td>
              </tr>
            ) : (
              rateCards.map((c) => {
                const avg = c.roles.length
                  ? c.roles.reduce((s, r) => s + Number(r.hourlyRate || 0), 0) / c.roles.length
                  : 0;
                const sym = c.currency?.symbol || "$";
                return (
                  <tr key={c.id} className="border-b border-gray-200 hover:bg-[#F9FAFB]" data-testid={`ratecard-row-${c.id}`}>
                    <td className="py-3 px-3">
                      <div className="font-bold text-black">{c.name}</div>
                      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-mono">v{c.version || 1}</div>
                    </td>
                    <td className="py-3 px-3">
                      <StatusBadge status={c.status || "draft"} card={c} />
                    </td>
                    <td className="py-3 px-3 text-gray-600 max-w-xs truncate">{c.description || "—"}</td>
                    <td className="py-3 px-3 font-mono text-xs">{c.currency?.code || "USD"}</td>
                    <td className="py-3 px-3 font-mono text-right">{fmtPercent(c.overheadPercent)}</td>
                    <td className="py-3 px-3 font-mono text-right">{fmtPercent(c.contingencyPercent)}</td>
                    <td className="py-3 px-3 font-mono text-right">{c.roles.length}</td>
                    <td className="py-3 px-3 font-mono text-right">{fmtCurrency(avg, sym)}</td>
                    <td className="py-2 px-3 text-right">
                      <div className="inline-flex items-center gap-1 flex-wrap justify-end">
                        {(c.status === "draft" || c.status === "rejected" || !c.status) && (
                          <button onClick={() => onSubmitForApproval(c)} data-testid={`ratecard-submit-${c.id}`}
                            className="bg-[#CA8A04] text-white hover:bg-black rounded-none px-2.5 py-1.5 font-bold text-xs">
                            Submit
                          </button>
                        )}
                        {c.status === "pending" && (
                          <>
                            <button onClick={() => onApprove(c)} data-testid={`ratecard-approve-${c.id}`}
                              className="bg-[#15803D] text-white hover:bg-black rounded-none px-2.5 py-1.5 font-bold text-xs">
                              Approve
                            </button>
                            <button onClick={() => onReject(c)} data-testid={`ratecard-reject-${c.id}`}
                              className="bg-white border border-[#FF2400] text-[#FF2400] hover:bg-red-50 rounded-none px-2.5 py-1.5 font-bold text-xs">
                              Reject
                            </button>
                          </>
                        )}
                        <button onClick={() => onApply(c)} data-testid={`ratecard-apply-${c.id}`}
                          className="bg-black text-white hover:bg-[#002FA7] rounded-none px-2.5 py-1.5 font-bold text-xs inline-flex items-center gap-1">
                          <CheckSquare size={12} /> Apply
                        </button>
                        <button onClick={() => startEdit(c)} data-testid={`ratecard-edit-${c.id}`}
                          className="bg-white border border-gray-300 text-black hover:bg-gray-100 rounded-none px-2.5 py-1.5 font-bold text-xs inline-flex items-center gap-1">
                          <Pencil size={12} /> Edit
                        </button>
                        <button onClick={() => onDelete(c)} data-testid={`ratecard-delete-${c.id}`}
                          className="bg-white border border-gray-300 text-[#FF2400] hover:bg-red-50 rounded-none px-2.5 py-1.5 font-bold text-xs inline-flex items-center gap-1">
                          <Trash2 size={12} /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const STATUS_STYLES = {
  draft:    { cls: "bg-gray-200 text-gray-800",     label: "DRAFT" },
  pending:  { cls: "bg-[#CA8A04] text-white",        label: "PENDING" },
  approved: { cls: "bg-[#15803D] text-white",        label: "APPROVED" },
  rejected: { cls: "bg-[#FF2400] text-white",        label: "REJECTED" },
};

function StatusBadge({ status, card }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.draft;
  const tooltip = status === "approved" && card?.approvedBy
    ? `Approved by ${card.approvedBy} on ${(card.approvedAt || "").slice(0, 10)}`
    : status === "pending" && card?.submittedBy
      ? `Submitted by ${card.submittedBy} on ${(card.submittedAt || "").slice(0, 10)}`
      : status === "rejected" && card?.rejectedReason
        ? `Rejected: ${card.rejectedReason}`
        : null;
  return (
    <span
      className={`inline-block px-2 py-0.5 font-mono text-[10px] font-bold tracking-wider ${s.cls}`}
      title={tooltip || undefined}
      data-testid={`ratecard-status-${status}`}
    >
      {s.label}
    </span>
  );
}
